import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useCurrentUser, useAccessToken } from '@/features/auth/state/auth'
import type { Alert } from '@/types/alert'

const BASE_URL = import.meta.env.VITE_API_BASE_URL
const api = axios.create({ baseURL: BASE_URL })

type ResolveAlertInput = {
  id: string
  message?: string
  targetUserId?: string
}

// Hook para buscar todos os alertas por cliente
export function useFetchAlertsByClient(id_client: string) {
  const token = useAccessToken()

  return useQuery({
    queryKey: ['alerts', 'client', id_client],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/alerts/client/${id_client}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(token && id_client)
  })
}

// Hook para buscar alertas não lidos por usuário
export function useFetchUnreadAlertsByUser() {
  const token = useAccessToken()
  const user = useCurrentUser()

  return useQuery({
    queryKey: ['alerts', 'me', user?.id],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/alerts/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = response.data?.data
      if (Array.isArray(data)) {
        return data as Alert[]
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(token && user?.id)
  })
}

// Hook para criar um novo alerta
export function useCreateAlert() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async newAlert => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.post('/alerts', newAlert, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao criar alerta')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

// Hook para marcar alerta como lido
export function useMarkAlertAsRead() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (alertId: string) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.patch(`/alerts/${alertId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } })

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao marcar alerta como lido')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

// Hook para marcar alerta como resolvido
export function useMarkAlertAsResolved() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, message, targetUserId }: ResolveAlertInput) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.patch(
        `/alerts/${id}/resolve`,
        { message, targetUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao marcar alerta como resolvido')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

export function useContestAlert() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, message, targetUserId }: ResolveAlertInput) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.patch(
        `/alerts/${id}/contest`,
        { message, targetUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao contestar alerta')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

// Hook para buscar alertas resolvidos por cliente
export function useFetchResolvedAlertsByClient(id_client: string) {
  const token = useAccessToken()

  return useQuery({
    queryKey: ['alerts', 'client', id_client, 'resolved'],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/alerts/client/${id_client}/resolved`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(token && id_client)
  })
}
